/**
 * constants.ts - Shared constants for the chess tournament application
 *
 * Centralizes magic strings and values used across the application.
 * TypeScript version with type safety.
 */

// API Base URL - dynamically set based on environment
// In development: http://localhost:3000
// In production: uses current domain (Railway URL or custom domain)
function getApiBaseUrl(): string {
  // Use current domain in production, localhost in development
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'http://localhost:3000';
}

const BASE_URL = getApiBaseUrl();

// API Base URLs for different sections
export const API_URL = `${BASE_URL}/api`;
export const API_BASE_URL = `${BASE_URL}/api/tournaments`;
export const AUTH_API_BASE = `${BASE_URL}/api/auth`;
export const SUBSCRIPTION_API_BASE = `${BASE_URL}/api/subscriptions`;

// Match Results
export const RESULT_TYPES = {
  WHITE_WIN: '1-0',
  BLACK_WIN: '0-1',
  DRAW: '0.5-0.5',
  WHITE_FORFEIT: '0-1ff',
  BLACK_FORFEIT: '1-0ff',
  DOUBLE_FORFEIT: '0-0'
} as const;

export type ResultType = typeof RESULT_TYPES[keyof typeof RESULT_TYPES];

// Tournament Formats
export const TOURNAMENT_FORMATS = {
  SWISS: 'swiss',
  ROUND_ROBIN: 'round-robin',
  KNOCKOUT: 'knockout'
} as const;

export type TournamentFormat = typeof TOURNAMENT_FORMATS[keyof typeof TOURNAMENT_FORMATS];

// Tournament Status
export const TOURNAMENT_STATUS = {
  UPCOMING: 'upcoming',
  ONGOING: 'ongoing',
  COMPLETED: 'completed'
} as const;

export type TournamentStatus = typeof TOURNAMENT_STATUS[keyof typeof TOURNAMENT_STATUS];

// Player Databases
export const PLAYER_DATABASES = {
  FIDE: 'fide',
  SWISS_CHESS: 'swisschess'
} as const;

export type PlayerDatabase = typeof PLAYER_DATABASES[keyof typeof PLAYER_DATABASES];

// Default Values
export const DEFAULTS = {
  TOURNAMENT_LOGO: '/images/tournament-logo.png',
  INITIAL_RATING: 1500,
  MIN_PLAYERS_SWISS: 4,
  MIN_PLAYERS_ROUND_ROBIN: 3,
  MIN_PLAYERS_KNOCKOUT: 2
} as const;

// CSS Classes
export const CSS_CLASSES = {
  STATUS_UPCOMING: 'status-upcoming',
  STATUS_ONGOING: 'status-ongoing',
  STATUS_COMPLETED: 'status-completed',
  BTN_PRIMARY: 'btn-primary',
  BTN_SECONDARY: 'btn-secondary',
  BTN_DANGER: 'btn-danger',
  BTN_SMALL: 'btn-small',
  BTN_ICON: 'btn-icon',
  ACTIVE: 'active',
  WINNER: 'winner',
  LOSER: 'loser'
} as const;

// Animation Durations (milliseconds)
export const ANIMATION_DURATIONS = {
  FLASH_SUCCESS: 1000,
  FLASH_ERROR: 1500,
  FADE: 300,
  SLIDE: 400
} as const;

// API Endpoints (relative to API_BASE_URL)
export const API_ENDPOINTS = {
  TOURNAMENTS: '',
  PLAYERS: (tournamentId: string) => `/${tournamentId}/players`,
  MATCHES: (tournamentId: string) => `/${tournamentId}/matches`,
  PAIRINGS: (tournamentId: string) => `/${tournamentId}/pairings`,
  STANDINGS: (tournamentId: string) => `/${tournamentId}/standings`,
  RANKING: (tournamentId: string) => `/${tournamentId}/ranking`,
  MATCH_RESULT: (matchId: string) => `/matches/${matchId}/result`,
  PLAYER_SEARCH_FIDE: '/players/fide/search',
  PLAYER_SEARCH_SWISS: '/players/swisschess/search'
} as const;

// Validation Rules
export const VALIDATION = {
  MIN_TOURNAMENT_NAME_LENGTH: 3,
  MAX_TOURNAMENT_NAME_LENGTH: 100,
  MIN_PLAYER_NAME_LENGTH: 2,
  MAX_PLAYER_NAME_LENGTH: 50,
  MIN_RATING: 0,
  MAX_RATING: 3500,
  RATING_STEP: 1
} as const;

// Date Formats
export const DATE_FORMATS = {
  SHORT: { month: 'short', day: 'numeric' } as const,
  FULL: { month: 'short', day: 'numeric', year: 'numeric' } as const,
  LONG: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' } as const
} as const;

// Tournament Tab IDs
export const TAB_IDS = {
  OVERVIEW: 'overview',
  PLAYERS: 'players',
  PAIRINGS: 'pairings',
  STANDINGS: 'standings',
  SCHEDULE: 'schedule'
} as const;

export type TabId = typeof TAB_IDS[keyof typeof TAB_IDS];

// Knockout Round Names (from end)
export const KNOCKOUT_ROUNDS = {
  1: 'Final',
  2: 'Semi-Final',
  3: 'Quarter-Final',
  4: 'Round of 16',
  5: 'Round of 32',
  6: 'Round of 64'
} as const;

// Special Player ID
export const SPECIAL_PLAYER_IDS = {
  BYE: 'BYE',
  TBD: 'TBD'
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  USER_DATA: 'userData',
  LANGUAGE: 'language',
  VIEW_PREFERENCES: 'viewPreferences'
} as const;

export type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];
